// One receipt: what it says, whether Chase agrees, who paid, what it was
// for, every item with its kind, the original, and Send / Download / Void.
// Loads its own detail when it opens (the original's short-lived link comes
// with it); every change goes through the receipts function and is logged on
// the server with the signed-in user as actor.
import { useCallback, useEffect, useState } from 'react';
import { receiptsCall } from './receiptsApi';
import { KINDS, KIND_LABELS, KIND_COLORS, kindOf } from './kindGroups';
import { tripByKind, itemsBasis, printedName, storeNames } from './receiptsModel';
import SendResult from './SendResult';
import { usd, longDate } from './money';
import { matchTag } from './matchTag';
import { cardFromDetail, paidByText } from './cardText';
import { itemLabel } from './itemName';
import { NotePanel, EmailPanel } from './ReceiptNote';
import BackupBanner from './BackupBanner';
import './Receipts.css';

const CONTEXTS = ['Personal', 'FIG', 'Monduras', 'Split'];
const CONFIRMED = new Set(['rcpt', 'loose', 'human']);

function KindPicker({ line, onPick, busy }) {
  const [open, setOpen] = useState(false);
  const k = kindOf(line);
  const label = line.kind ? KIND_LABELS[k] : 'Unlabeled';
  return open ? (
    <select className="rc-select" autoFocus disabled={busy} defaultValue={line.kind || ''}
      onBlur={() => setOpen(false)}
      onChange={(e) => { setOpen(false); if (e.target.value && e.target.value !== line.kind) onPick(e.target.value); }}>
      <option value="" disabled>Kind…</option>
      {KINDS.map((x) => <option key={x} value={x}>{KIND_LABELS[x]}</option>)}
    </select>
  ) : (
    <button type="button" className={`rc-kind${line.kindSource === 'human' ? ' rc-kind--human' : ''}`}
      title={line.kindSource === 'human' ? 'Set by hand · tap to change' : line.kindSource === 'memory' ? 'Remembered for this store · tap to change' : 'Tap to change'}
      style={{ background: `${KIND_COLORS[k]}1f`, color: KIND_COLORS[k] }} onClick={() => setOpen(true)}>
      {label}{line.kindDetail ? ` · ${line.kindDetail}` : ''}
    </button>
  );
}

export default function ReceiptScreen({ id, backLabel = 'Finance', onBack, onOpenKind }) {
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [ctxOpen, setCtxOpen] = useState(false);
  const [owedBy, setOwedBy] = useState('');
  const [voiding, setVoiding] = useState(false);
  const [reason, setReason] = useState('');
  const [sent, setSent] = useState(null);

  const load = useCallback(async () => {
    setErr(null);
    try {
      const x = await receiptsCall('detail', { id });
      setD(x);
      setOwedBy(x.receipt.owedBy || '');
    } catch (e) { setErr(e.message); }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const act = async (op, payload, done) => {
    setBusy(true); setMsg(null);
    try {
      const out = await receiptsCall(op, { id, ...payload });
      if (done) done(out);
      await load();
    } catch (e) { setMsg({ err: true, text: e.message }); }
    setBusy(false);
  };

  // The note is saved here, not through act: the panel closes only when it saved.
  const saveNote = async (note) => {
    setBusy(true); setMsg(null);
    let ok = false;
    try { await receiptsCall('set', { id, note }); await load(); ok = true; } catch (e) { setMsg({ err: true, text: e.message }); }
    setBusy(false);
    return ok;
  };

  if (err) return <div className="fin-root rc-page"><button className="rc-back" onClick={onBack}>← {backLabel}</button><p className="rc-error">⚠ {err}</p><button className="fin-retry" onClick={load}>Retry</button></div>;
  if (!d) return <div className="fin-root rc-page"><button className="rc-back" onClick={onBack}>← {backLabel}</button><div className="fin-state"><div className="fin-spinner" /><p>Loading receipt…</p></div></div>;

  const r = d.receipt;
  const isVoid = r.status === 'void';
  const cardInfo = cardFromDetail(d);
  const m = matchTag(r, d.linkedCharge, cardInfo);
  const confirmed = CONFIRMED.has(r.tagSource);
  const trip = tripByKind(d.lines, r);
  // Lines already paid net (Kroger): a printed discount is shown as saved, not taken off again.
  const grossLines = itemsBasis(r).subtractLineDiscounts;
  const items = r.subtotal !== null && r.subtotal !== undefined ? (r.discount ? Math.round((r.subtotal - r.discount) * 100) / 100 : r.subtotal) : null;
  const card = d.card;

  return (
    <div className="fin-root rc-page">
      <button className="rc-back" onClick={onBack}>← {backLabel}</button>
      <BackupBanner refused={d.backupRefused} />
      <div className="rc-head">
        <div>
          <h2 className="rc-title">{storeNames(r).shown || 'Merchant not read'}</h2>
          {printedName(r) && <div className="rc-printed">as printed: {printedName(r)}</div>}
          <div className="rc-sub">{[longDate(r.purchaseDate), r.storeCity, r.id].filter(Boolean).join(' · ')}</div>
        </div>
        <div className="rc-big">{usd(r.total)}</div>
      </div>

      <div className="rc-chips">
        <span className={`rc-tag ${m.cls}`}>{m.cls === 'rc-tag--ok' ? '✓ ' : ''}{m.text}</span>
        {r.cardLast4 && <span className={`rc-tag${cardInfo.cardKnown === false ? ' rc-tag--amber' : ''}`} title="Who paid">{paidByText(cardInfo)}{r.cardLast4Basis === 'reference' && card?.status !== 'confirmed' ? '?' : ''}</span>}
        {card?.accountMask && <span className="rc-tag">Account ····{card.accountMask}</span>}
        {!isVoid && (confirmed && r.tagContext
          ? <button type="button" className="rc-tag" onClick={() => setCtxOpen((o) => !o)} title="Change">{r.tagContext}</button>
          : <button type="button" className="rc-tag rc-tag--amber" onClick={() => setCtxOpen((o) => !o)}>{r.tagContext ? `${r.tagContext}? Tap to confirm` : 'Context? Tap to set'}</button>)}
        {r.changedAfterSendAt && <span className="rc-tag rc-tag--amber" title={`Context changed ${r.changedAfterSendAt.slice(0, 10)}, after it went out in a draft`}>Changed after send</span>}
        {isVoid && <span className="rc-tag rc-tag--bad">Void{r.voidReason ? ` · ${r.voidReason}` : ''}</span>}
      </div>

      {ctxOpen && !isVoid && (
        <div className="rc-actions" role="group" aria-label="Context">
          {CONTEXTS.map((c) => (
            <button key={c} type="button" disabled={busy} className={`rc-seg${r.tagContext === c && confirmed ? ' rc-seg--on' : ''}`}
              onClick={() => act('set', { context: c }, () => setCtxOpen(false))}>{c}</button>
          ))}
        </div>
      )}

      {!isVoid && (
        <div className="rc-actions" style={{ alignItems: 'center' }}>
          <label style={{ fontSize: '0.82rem', color: '#5a3a10', display: 'flex', alignItems: 'center', gap: 6 }}>
            <input type="checkbox" checked={r.owed === true} disabled={busy} onChange={(e) => act('set', { owed: e.target.checked, ...(e.target.checked && owedBy.trim() ? { owedBy: owedBy.trim() } : {}) })} />
            Owed to me
          </label>
          {r.owed === true && (
            <>
              <input className="rc-input" placeholder="Who owes" value={owedBy} onChange={(e) => setOwedBy(e.target.value)} style={{ width: 140 }} />
              <button className="rc-btn rc-btn--small" disabled={busy || owedBy.trim() === (r.owedBy || '')} onClick={() => act('set', { owed: true, owedBy: owedBy.trim() || null })}>Save</button>
            </>
          )}
          <span style={{ flex: 1 }} />
          <button className="rc-btn rc-btn--primary" disabled={busy} onClick={() => act('send', { ids: [id] }, (out) => setSent(out))}>✉ Send</button>
          {d.files.length > 0 && <button className="rc-btn" onClick={() => d.files.forEach((f) => f.downloadUrl && window.open(f.downloadUrl, '_blank', 'noopener'))}>Download</button>}
          <button className="rc-btn rc-btn--danger" disabled={busy} onClick={() => setVoiding((v) => !v)}>Void</button>
        </div>
      )}
      {sent && <SendResult result={sent} onClose={() => setSent(null)} />}
      {msg && <p className={msg.err ? 'rc-error' : 'rc-ok'}>{msg.err ? '⚠ ' : ''}{msg.text}</p>}
      {voiding && !isVoid && (
        <div className="rc-panel rc-panel--beige">
          <div style={{ fontSize: '0.82rem', marginBottom: 6 }}>Void this receipt? It stays on file, marked void{d.linkedCharge ? ', and its Chase charge is released' : ''}.</div>
          <div className="rc-actions" style={{ marginBottom: 0 }}>
            <input className="rc-input" placeholder="Reason (required)" value={reason} onChange={(e) => setReason(e.target.value)} style={{ flex: 1, minWidth: 160 }} />
            <button className="rc-btn rc-btn--danger" disabled={busy || !reason.trim()} onClick={() => act('void', { reason: reason.trim() }, () => { setVoiding(false); setReason(''); })}>Void receipt</button>
            <button className="rc-btn" onClick={() => setVoiding(false)}>Cancel</button>
          </div>
        </div>
      )}

      <NotePanel note={r.note} busy={busy} disabled={isVoid} onSave={saveNote} />
      <EmailPanel email={d.email} />

      <div className="rc-grid">
        <div>
          <h3 className="fin-section-title">What's inside</h3>
          {d.lines.length === 0 ? <p className="rc-note">No items were read on this receipt.</p> : (
            <table className="rc-items">
              <tbody>
                {d.lines.map((l) => (
                  <tr key={l.lineNo}>
                    <td>
                      <span className="rc-item-name">{itemLabel(l)}</span>
                      {l.description && l.description !== itemLabel(l) && <span className="rc-item-raw rc-hide-sm">{l.description}</span>}
                    </td>
                    <td><KindPicker line={l} busy={busy || isVoid} onPick={(k) => act('kind', { lineNo: l.lineNo, kind: k })} /></td>
                    <td className="num" style={{ color: '#7a6a5a' }}>{l.quantity ?? ''}{l.unit && l.unit !== 'each' ? ` ${l.unit}` : ''}</td>
                    <td className="num">
                      {usd(l.amount)}
                      {l.discount ? <span className="rc-disc">{grossLines ? `−${usd(l.discount)}` : `saved ${usd(l.discount)}`}</span> : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <div className="rc-totals">
            {items !== null && <div><span>Items{r.discount ? `, after ${usd(r.discount)} savings` : ''}</span><span>{usd(items)}</span></div>}
            {r.tax !== null && r.tax !== undefined ? <div><span>Tax</span><span>{usd(r.tax)}</span></div> : <div><span>Tax</span><span className="rc-notax">no tax line</span></div>}
            {r.tip !== null && r.tip !== undefined && <div><span>Tip</span><span>{usd(r.tip)}</span></div>}
            <div className="rc-total"><span>Total</span><span>{usd(r.total)}</span></div>
          </div>
          <p className="rc-note">Tap a kind to fix it; the fix is remembered for this store. Tap a kind in “This trip” to see everything like it.</p>
        </div>

        <div>
          {trip.length > 0 && (
            <div className="rc-panel rc-panel--beige">
              <div style={{ fontWeight: 700, fontSize: '0.85rem', color: '#2a1a0a' }}>This trip by kind</div>
              <div className="rc-stack">{trip.map((t) => <span key={t.kind} style={{ width: `${t.share * 100}%`, background: KIND_COLORS[t.kind] }} title={KIND_LABELS[t.kind]} />)}</div>
              {trip.map((t) => (
                <div className="rc-row" key={t.kind}>
                  <button type="button" onClick={() => onOpenKind && onOpenKind(t.kind)}><span className="rc-dot" style={{ background: KIND_COLORS[t.kind] }} />{KIND_LABELS[t.kind]}</button>
                  <strong>{usd(t.amount)}</strong>
                </div>
              ))}
            </div>
          )}
          {d.files.map((f) => (
            <div key={f.name} style={{ marginBottom: '0.75rem' }}>
              {f.viewImage ? <img className="rc-original" src={f.viewImage} alt={`Receipt ${r.id}`} />
                : f.kind === 'pdf' && f.url ? <iframe className="rc-original rc-original--pdf" src={f.url} title={`Receipt ${r.id}`} />
                : f.url ? <img className="rc-original" src={f.url} alt={`Receipt ${r.id}`} />
                : <div className="rc-panel rc-note">This file cannot be shown here. Use Download.</div>}
              {f.viewImage && <p className="rc-note">Shown as a JPEG copy; Download gives the original.</p>}
            </div>
          ))}
        </div>
      </div>

      <details className="rc-history">
        <summary>History ({d.history.length})</summary>
        <ul>
          {d.history.slice().reverse().map((h, i) => (
            <li key={i}>{h.at.slice(0, 16).replace('T', ' ')} · {h.actor} · {h.field}{h.after_json && h.after_json.length < 80 ? ` → ${h.after_json}` : ''}</li>
          ))}
        </ul>
      </details>
    </div>
  );
}
